import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { Loading } from '../components/Loading';
import { EmptyState } from '../components/EmptyState';

const STATUS_META = {
  pending: { label: 'Pedido pendente', emoji: '🕐', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
  accepted: { label: 'Pedido aceito', emoji: '✅', color: 'var(--nc-primary)', bg: 'var(--nc-success-soft)' },
  picked_up: { label: 'Em entrega', emoji: '🛵', color: '#1e4d8c', bg: '#eff6ff' },
  delivered: { label: 'Pedido entregue', emoji: '📦', color: 'var(--nc-success-text)', bg: 'var(--nc-success-soft)' },
  cancelled: { label: 'Pedido cancelado', emoji: '🚫', color: 'var(--nc-danger-text)', bg: 'var(--nc-danger-soft)' },
  declined: { label: 'Recusado — aguardando outro entregador', emoji: '✋', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' },
};

// LE-014: página de notificações — exibe as atualizações reais dos pedidos de
// entrega do usuário (não há sistema de push no MVP; nada é inventado).
export function NotificationsPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/orders');
      setOrders((data.data || []).slice(0, 20));
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  return (
    <div className="nc-page nc-page--pad">
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'var(--nc-surface)', borderBottom: '1px solid var(--nc-border)', padding: '12px 16px',
        display: 'flex', alignItems: 'center', gap: 12
      }}>
        <button
          onClick={() => navigate('/perfil')}
          aria-label="Voltar"
          style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', display: 'flex' }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1f2937" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 style={{ fontSize: 16, fontWeight: 600, color: 'var(--nc-text)', margin: 0 }}>Notificações</h1>
      </div>

      <div className="nc-container" style={{ paddingTop: 16, paddingBottom: 16 }}>
        {loading ? <Loading message="Carregando notificações..." /> : error ? (
          <div role="alert" style={{
            padding: 14, borderRadius: 10, background: 'var(--nc-danger-soft)', border: '1px solid var(--nc-danger-border)',
            color: 'var(--nc-danger-text)', fontSize: 13
          }}>
            {error}
            <button onClick={fetchNotifications} className="nc-btn-primary" style={{ marginTop: 10, width: '100%' }}>Tentar novamente</button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            icon="🔔"
            title="Nenhuma notificação"
            description="As atualizações dos seus pedidos de entrega aparecerão aqui. Sem novidades por enquanto!"
            action={
              <button className="nc-btn-primary" onClick={() => navigate('/entregas')}>Ver entregadores</button>
            }
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {orders.map(o => {
              // LE-019: pedido expirado na fila = 'cancelled' + expired_at
              const isExpired = o.status === 'cancelled' && !!o.expired_at;
              const meta = isExpired
                ? { label: 'Pedido expirado — sem entregador disponível', emoji: '⏰', color: 'var(--nc-warning-text)', bg: 'var(--nc-warning-soft)' }
                : STATUS_META[o.status] || STATUS_META.pending;
              return (
                <div key={o.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: 12,
                  background: 'var(--nc-surface)', borderRadius: 10, border: '1px solid var(--nc-border)'
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%', background: meta.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                  }}>{meta.emoji}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: meta.color }}>{meta.label}</div>
                    <div className="nc-hint">
                      Pedido #{o.id}{o.business_name ? ` • ${o.business_name}` : ''}
                    </div>
                    <div className="nc-caption">
                      {new Date(o.created_at).toLocaleDateString('pt-BR')} {new Date(o.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <button onClick={() => navigate('/pedidos')} style={{
                    background: 'none', border: 'none', color: 'var(--nc-primary)', fontSize: 12, fontWeight: 500, cursor: 'pointer'
                  }}>Ver</button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
